import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState, type FormEvent } from "react";
import { api } from "../api/cliente";
import { chaves, useMunicipios, useSindicatos } from "../api/consultas";
import { podeEditar, useAuth } from "../auth/AuthProvider";
import { useAvisos } from "../components/Avisos";
import { Carregando, ErroCarregamento, semAutocompletar, Tabela } from "../components/ui";

export function Municipios() {
  const municipios = useMunicipios();
  const sindicatos = useSindicatos();
  const { usuario } = useAuth();
  const { avisar, avisarErro } = useAvisos();
  const queryClient = useQueryClient();
  const [filtro, setFiltro] = useState("");
  const [form, setForm] = useState({ nome: "", sindicato_id: "", taxa_iss: "5.00" });

  const salvar = useMutation({
    mutationFn: () => api<{ mensagem: string }>("/api/municipios", { method: "PUT", body: form }),
    onSuccess: (r) => {
      avisar(r.mensagem);
      setForm({ nome: "", sindicato_id: "", taxa_iss: "5.00" });
      queryClient.invalidateQueries({ queryKey: chaves.municipios });
      queryClient.invalidateQueries({ queryKey: chaves.painel });
    },
    onError: avisarErro,
  });

  const visiveis = useMemo(
    () => (municipios.data ?? []).filter((m) => m.nome.toLowerCase().includes(filtro.toLowerCase())),
    [municipios.data, filtro],
  );

  if (municipios.isLoading || sindicatos.isLoading) return <Carregando />;
  if (municipios.error || sindicatos.error) return <ErroCarregamento erro={municipios.error ?? sindicatos.error} />;
  const listaSindicatos = sindicatos.data ?? [];

  return (
    <>
      <h1>Municípios × Sindicato / ISS</h1>
      <p className="note">
        A companhia publica, para cada família de serviço, uma variante de preços por sindicato regional (o valor de encargos
        sociais "Leis Sociais" muda conforme a convenção coletiva local). Os 415 municípios abaixo foram carregados do
        documento oficial "Área de Abrangência dos Sindicatos" (data-base Junho/2026) — cada linha mostra a observação/fonte
        do vínculo. Municípios da faixa "CCT Interior" aparecem com sindicato SINTRACOM porque compartilham os mesmos
        encargos sociais. A Taxa de ISS (define a faixa de BDI) ainda precisa ser preenchida caso a caso — só Salvador está
        confirmada (5%, citada no próprio documento de BDI).
      </p>

      {podeEditar(usuario) && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Adicionar / atualizar município</h2>
          <form
            className="form-row"
            onSubmit={(ev: FormEvent) => {
              ev.preventDefault();
              salvar.mutate();
            }}
          >
            <label>
              Município
              <input type="text" required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} {...semAutocompletar} />
            </label>
            <label>
              Sindicato
              <select value={form.sindicato_id} onChange={(e) => setForm({ ...form, sindicato_id: e.target.value })}>
                <option value="">— não definido —</option>
                {listaSindicatos.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.sigla}
                    {s.base_territorial ? ` (${s.base_territorial})` : ""}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Taxa de ISS (%)
              <select value={form.taxa_iss} onChange={(e) => setForm({ ...form, taxa_iss: e.target.value })}>
                <option value="5.00">5,00%</option>
                <option value="4.00">4,00%</option>
                <option value="3.00">3,00%</option>
                <option value="2.00">2,00%</option>
              </select>
            </label>
            <button type="submit" disabled={salvar.isPending}>
              Salvar
            </button>
          </form>
        </div>
      )}

      <div className="card">
        <div className="form-row">
          <label>
            Filtrar
            <input type="search" placeholder="nome do município..." style={{ minWidth: 260 }} value={filtro} onChange={(e) => setFiltro(e.target.value)} {...semAutocompletar} />
          </label>
          <span className="note" style={{ alignSelf: "end" }}>
            {visiveis.length} de {municipios.data?.length ?? 0} municípios
          </span>
        </div>
        <Tabela>
          <thead>
            <tr>
              <th>Município</th>
              <th>Sindicato</th>
              <th>ISS</th>
              <th>Status</th>
              <th>Observação / fonte</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((m) => (
              <tr key={m.id}>
                <td>{m.nome}</td>
                <td>{m.sindicato_sigla || "—"}</td>
                <td>{m.taxa_iss !== null ? `${m.taxa_iss}%` : "—"}</td>
                <td>
                  {m.confirmado ? <span className="badge badge-green">confirmado</span> : <span className="badge badge-muted">não confirmado</span>}
                </td>
                <td className="note">{m.observacoes || "—"}</td>
              </tr>
            ))}
          </tbody>
        </Tabela>
      </div>

      <h2>Sindicatos conhecidos (Serviços de Expansão, Jun/2026-R2)</h2>
      <div className="card">
        <Tabela>
          <thead>
            <tr>
              <th>Sigla</th>
              <th>LS Horista</th>
              <th>LS Mensalista</th>
              <th>Base territorial</th>
            </tr>
          </thead>
          <tbody>
            {listaSindicatos.map((s) => (
              <tr key={s.id}>
                <td>
                  <span className="badge badge-blue">{s.sigla}</span>
                </td>
                <td className="num">{s.ls_h ?? "—"}%</td>
                <td className="num">{s.ls_m ?? "—"}%</td>
                <td>{s.base_territorial || <span className="badge badge-muted">a confirmar</span>}</td>
              </tr>
            ))}
          </tbody>
        </Tabela>
      </div>
    </>
  );
}
